import { useState, useEffect } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { VoiceToTextTextarea as Textarea } from "@/components/voice-field";
import { Card, CardContent } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Plus, Trash2, Sparkles, Loader2 } from "lucide-react";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import type { QualificationProfileConfig, QualificationSignal, QualificationDisqualifier, ClientTypeQualificationProfile } from "@shared/schema";

const EMPTY_CONFIG: QualificationProfileConfig = {
  positiveSignals: [],
  negativeSignals: [],
  disqualifiers: [],
  thresholds: { unqualified: 0, low: 1, fair: 2, good: 4, strong: 5 },
  prioritizedSources: [],
  customInstructions: "",
};

function uid() { return Math.random().toString(36).slice(2, 10); }

interface Props {
  clientTypeId: string;
  clientTypeName: string;
}

type ChatTurn = { role: "user" | "assistant"; content: string };

export function QualificationProfileSection({ clientTypeId, clientTypeName }: Props) {
  const { toast } = useToast();
  const [config, setConfig] = useState<QualificationProfileConfig>(EMPTY_CONFIG);
  const [isActive, setIsActive] = useState(true);
  const [helperOpen, setHelperOpen] = useState(false);
  const [helperPrompt, setHelperPrompt] = useState("");
  const [chatTurns, setChatTurns] = useState<ChatTurn[]>([]);

  const { data, isLoading } = useQuery<{ profile: ClientTypeQualificationProfile | null }>({
    queryKey: ["/api/client-types", clientTypeId, "qualification-profile"],
    enabled: !!clientTypeId,
  });

  // Hydrate local state when the server returns a profile.
  useEffect(() => {
    if (data?.profile) {
      setConfig((data.profile.config as QualificationProfileConfig) || EMPTY_CONFIG);
      setIsActive(data.profile.isActive ?? true);
    }
  }, [data?.profile?.id]);

  const saveMutation = useMutation({
    mutationFn: async () => apiRequest("PUT", `/api/client-types/${clientTypeId}/qualification-profile`, { config, isActive }),
    onSuccess: () => {
      toast({ title: "Profile saved" });
      queryClient.invalidateQueries({ queryKey: ["/api/client-types", clientTypeId, "qualification-profile"] });
    },
    onError: (e: any) => toast({ title: "Save failed", description: e?.message, variant: "destructive" }),
  });

  const applyProfile = (s: any) => {
    if (!s || typeof s !== "object") return;
    setConfig((prev) => ({
      positiveSignals: [
        ...prev.positiveSignals,
        ...((s.positiveSignals || []) as Array<any>).map((x) => ({ id: uid(), label: x.name || x.label, weight: Number(x.weight) || 3, hint: x.rationale || x.hint })),
      ],
      negativeSignals: [
        ...prev.negativeSignals,
        ...((s.negativeSignals || []) as Array<any>).map((x) => ({ id: uid(), label: x.name || x.label, weight: Number(x.weight) || 3, hint: x.rationale || x.hint })),
      ],
      disqualifiers: [
        ...prev.disqualifiers,
        ...((s.disqualifiers || []) as Array<any>).map((x) => ({ id: uid(), label: x.name || x.label, hint: x.rationale || x.hint })),
      ],
      thresholds: s.thresholds || prev.thresholds,
      prioritizedSources: [
        ...prev.prioritizedSources,
        ...((s.sources || s.prioritizedSources || []) as Array<any>).map((x) => (typeof x === "string" ? x : x.label)).filter(Boolean),
      ],
      customInstructions: prev.customInstructions
        ? `${prev.customInstructions}\n\n${s.instructions || s.customInstructions || ""}`.trim()
        : (s.instructions || s.customInstructions || ""),
    }));
  };

  const helperMutation = useMutation({
    mutationFn: async (nextTurns: ChatTurn[]) => {
      const res = await apiRequest("POST", `/api/client-types/${clientTypeId}/qualification-profile/ai-helper`, { messages: nextTurns });
      return res.json();
    },
    onSuccess: (data: any) => {
      const status = data?.status === "ready" ? "ready" : "asking";
      const message = String(data?.message || "");
      setChatTurns((prev) => [...prev, { role: "assistant", content: message }]);
      if (status === "ready") {
        applyProfile(data?.profile);
        toast({ title: "AI suggestion added", description: "Review and edit the proposed signals before saving." });
        setHelperOpen(false);
        setHelperPrompt("");
        setChatTurns([]);
      }
    },
    onError: (e: any) => toast({ title: "AI helper failed", description: e?.message, variant: "destructive" }),
  });

  const sendHelperTurn = () => {
    const text = helperPrompt.trim();
    if (text.length < 2) return;
    const nextTurns: ChatTurn[] = [...chatTurns, { role: "user", content: text }];
    setChatTurns(nextTurns);
    setHelperPrompt("");
    helperMutation.mutate(nextTurns);
  };

  const cancelHelper = () => {
    setHelperOpen(false);
    setHelperPrompt("");
    setChatTurns([]);
  };

  const updateSignal = (kind: "positiveSignals" | "negativeSignals", idx: number, patch: Partial<QualificationSignal>) => {
    setConfig((prev) => ({
      ...prev,
      [kind]: prev[kind].map((s, i) => (i === idx ? { ...s, ...patch } : s)),
    }));
  };
  const addSignal = (kind: "positiveSignals" | "negativeSignals") => {
    setConfig((prev) => ({ ...prev, [kind]: [...prev[kind], { id: uid(), label: "", weight: 3 }] }));
  };
  const removeSignal = (kind: "positiveSignals" | "negativeSignals", idx: number) => {
    setConfig((prev) => ({ ...prev, [kind]: prev[kind].filter((_, i) => i !== idx) }));
  };
  const addDisq = () => setConfig((p) => ({ ...p, disqualifiers: [...p.disqualifiers, { id: uid(), label: "" }] }));
  const updateDisq = (idx: number, patch: Partial<QualificationDisqualifier>) => {
    setConfig((p) => ({ ...p, disqualifiers: p.disqualifiers.map((d, i) => (i === idx ? { ...d, ...patch } : d)) }));
  };
  const removeDisq = (idx: number) => setConfig((p) => ({ ...p, disqualifiers: p.disqualifiers.filter((_, i) => i !== idx) }));

  if (isLoading) return <p className="text-sm text-muted-foreground">Loading qualification profile…</p>;

  return (
    <Card className="border-dashed">
      <CardContent className="pt-4 space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <h4 className="font-semibold flex items-center gap-2"><Sparkles className="w-4 h-4" /> AI Qualification Profile</h4>
            <p className="text-xs text-muted-foreground">Tells the AI what makes a "good" {clientTypeName} lead.</p>
          </div>
          <div className="flex items-center gap-2">
            <Label htmlFor="qual-active" className="text-xs">Active</Label>
            <Switch id="qual-active" checked={isActive} onCheckedChange={setIsActive} data-testid="switch-qualification-active" />
          </div>
        </div>

        {!helperOpen ? (
          <Button type="button" variant="outline" size="sm" onClick={() => setHelperOpen(true)} data-testid="button-open-ai-helper">
            <Sparkles className="w-4 h-4 mr-2" /> Help me build this profile
          </Button>
        ) : (
          <div className="space-y-3 rounded-md border p-3 bg-muted/30">
            {chatTurns.length === 0 ? (
              <Label htmlFor="ai-prompt" className="text-xs">
                Describe your ideal customer for this client type. The AI may ask follow-up questions before drafting the profile.
              </Label>
            ) : (
              <div className="space-y-2 max-h-64 overflow-y-auto" data-testid="chat-helper-thread">
                {chatTurns.map((t, i) => (
                  <div
                    key={i}
                    className={`text-sm rounded-md p-2 ${t.role === "user" ? "bg-background border" : "bg-primary/10 border border-primary/20"}`}
                    data-testid={`chat-turn-${t.role}-${i}`}
                  >
                    <div className="text-[10px] uppercase font-semibold text-muted-foreground mb-1">
                      {t.role === "user" ? "You" : "AI"}
                    </div>
                    <div className="whitespace-pre-wrap">{t.content}</div>
                  </div>
                ))}
                {helperMutation.isPending && (
                  <div className="text-xs text-muted-foreground flex items-center gap-2" data-testid="chat-helper-thinking">
                    <Loader2 className="w-3 h-3 animate-spin" /> AI is thinking…
                  </div>
                )}
              </div>
            )}
            <Textarea
              id="ai-prompt"
              value={helperPrompt}
              onChange={(e) => setHelperPrompt(e.target.value)}
              placeholder={
                chatTurns.length === 0
                  ? "e.g. Mid-size assisted living communities (50-150 residents) in the Southeast that book outside entertainment monthly and have a dedicated Activities Director."
                  : "Type your reply…"
              }
              rows={3}
              disabled={helperMutation.isPending}
              data-testid="textarea-ai-helper-prompt"
            />
            <div className="flex gap-2 justify-end">
              <Button type="button" variant="ghost" size="sm" onClick={cancelHelper} data-testid="button-cancel-ai-helper">Cancel</Button>
              <Button
                type="button"
                size="sm"
                disabled={helperPrompt.trim().length < 2 || helperMutation.isPending}
                onClick={sendHelperTurn}
                data-testid="button-run-ai-helper"
              >
                {helperMutation.isPending ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Sparkles className="w-4 h-4 mr-2" />}
                {chatTurns.length === 0 ? "Start" : "Send reply"}
              </Button>
            </div>
          </div>
        )}

        {/* Positive signals */}
        <div className="space-y-2">
          <Label className="text-xs font-semibold uppercase">Positive Signals</Label>
          {config.positiveSignals.map((s, i) => (
            <div key={s.id} className="flex gap-2 items-center" data-testid={`row-positive-${i}`}>
              <Input value={s.label} onChange={(e) => updateSignal("positiveSignals", i, { label: e.target.value })} placeholder="Signal name" className="flex-1" />
              <Input type="number" min={1} max={5} value={s.weight} onChange={(e) => updateSignal("positiveSignals", i, { weight: Number(e.target.value) || 1 })} className="w-16" />
              <Button type="button" variant="ghost" size="icon" onClick={() => removeSignal("positiveSignals", i)}><Trash2 className="w-4 h-4" /></Button>
            </div>
          ))}
          <Button type="button" variant="outline" size="sm" onClick={() => addSignal("positiveSignals")} data-testid="button-add-positive"><Plus className="w-4 h-4 mr-2" /> Add positive signal</Button>
        </div>

        {/* Negative signals */}
        <div className="space-y-2">
          <Label className="text-xs font-semibold uppercase">Negative Signals</Label>
          {config.negativeSignals.map((s, i) => (
            <div key={s.id} className="flex gap-2 items-center" data-testid={`row-negative-${i}`}>
              <Input value={s.label} onChange={(e) => updateSignal("negativeSignals", i, { label: e.target.value })} placeholder="Signal name" className="flex-1" />
              <Input type="number" min={1} max={5} value={s.weight} onChange={(e) => updateSignal("negativeSignals", i, { weight: Number(e.target.value) || 1 })} className="w-16" />
              <Button type="button" variant="ghost" size="icon" onClick={() => removeSignal("negativeSignals", i)}><Trash2 className="w-4 h-4" /></Button>
            </div>
          ))}
          <Button type="button" variant="outline" size="sm" onClick={() => addSignal("negativeSignals")} data-testid="button-add-negative"><Plus className="w-4 h-4 mr-2" /> Add negative signal</Button>
        </div>

        {/* Disqualifiers */}
        <div className="space-y-2">
          <Label className="text-xs font-semibold uppercase">Disqualifiers (rating → 0)</Label>
          {config.disqualifiers.map((d, i) => (
            <div key={d.id} className="flex gap-2 items-center" data-testid={`row-disq-${i}`}>
              <Input value={d.label} onChange={(e) => updateDisq(i, { label: e.target.value })} placeholder="Disqualifier" className="flex-1" />
              <Button type="button" variant="ghost" size="icon" onClick={() => removeDisq(i)}><Trash2 className="w-4 h-4" /></Button>
            </div>
          ))}
          <Button type="button" variant="outline" size="sm" onClick={addDisq} data-testid="button-add-disqualifier"><Plus className="w-4 h-4 mr-2" /> Add disqualifier</Button>
        </div>

        {/* Sources */}
        <div className="space-y-2">
          <Label htmlFor="sources" className="text-xs font-semibold uppercase">Prioritized Sources (one per line)</Label>
          <Textarea
            id="sources"
            rows={3}
            value={config.prioritizedSources.join("\n")}
            onChange={(e) => setConfig({ ...config, prioritizedSources: e.target.value.split("\n").map((x) => x.trim()).filter(Boolean) })}
            data-testid="textarea-sources"
          />
        </div>

        {/* Instructions */}
        <div className="space-y-2">
          <Label htmlFor="instructions" className="text-xs font-semibold uppercase">Custom Instructions for the AI</Label>
          <Textarea
            id="instructions"
            rows={4}
            value={config.customInstructions}
            onChange={(e) => setConfig({ ...config, customInstructions: e.target.value })}
            data-testid="textarea-instructions"
          />
        </div>

        {/* Thresholds */}
        <div className="space-y-2">
          <Label className="text-xs font-semibold uppercase">Rating Label Thresholds (0-5)</Label>
          <div className="grid grid-cols-5 gap-2">
            {(["unqualified", "low", "fair", "good", "strong"] as const).map((k) => (
              <div key={k}>
                <Label htmlFor={`th-${k}`} className="text-xs capitalize">{k}</Label>
                <Input
                  id={`th-${k}`}
                  type="number"
                  min={0}
                  max={5}
                  value={config.thresholds[k]}
                  onChange={(e) => setConfig({ ...config, thresholds: { ...config.thresholds, [k]: Number(e.target.value) } })}
                  data-testid={`input-threshold-${k}`}
                />
              </div>
            ))}
          </div>
        </div>

        <div className="flex justify-end">
          <Button type="button" size="sm" onClick={() => saveMutation.mutate()} disabled={saveMutation.isPending} data-testid="button-save-qualification-profile">
            {saveMutation.isPending ? "Saving…" : "Save Qualification Profile"}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
